"use client";

import React from "react";
import { firePdfDownload, firePhoneLead } from "../../services/analytics";
import styles from "./page-section-header.module.css";
/* ⚠ THE TESTS COME FROM ./page-section-header.shared, AND THAT IS THE POINT.
   The organism decides WHETHER a button gets this seam and the seam decides
   WHICH report to make, off the same address — so a literal kept on both sides
   is a destination one of them reports and the other misses. That module holds
   no React and no "use client", so a Server Component importing it gets the
   values rather than an opaque client reference, and this one drags no boundary
   across by reading it. */
import { isPdfCta, isPhoneCta } from "./page-section-header.shared";
import { withSafeRender } from "../../atoms/safe-render";

export interface SectionHeaderCtaProps {
  /** Where the button goes. It is the ONLY thing that decides which report is
   *  made, or whether one is made at all: a `tel:` address is a phone lead, an
   *  address ending `.pdf` is a download, and everything else is reported by
   *  the container alone. Absent, nothing is reported. */
  url?: string;
  /** The button's wording as it reads on screen. Sorts a phone lead into its
   *  audience, and names a download. Absent, the lead is reported as `new` and
   *  the download is named by its address.
   *
   *  ⚠ THE WORDING, NEVER THE AUTHORED `*_cta_aria_label`. The two are allowed
   *  to differ, and feeding the accessible name through here would re-bucket
   *  every phone lead the moment an author sets one. */
  label?: string;
  /** The button, already rendered by the organism. */
  children: React.ReactNode;
}

/**
 * Section Header CTA — the tracking seam around ONE of Page Section Header's
 * buttons. It draws nothing, holds no state, and reports exactly two clicks:
 * a call, and a document download.
 *
 * ⚠ IT NEVER REPORTS A `link_click`, AND TAKES NO PROP THAT COULD ASK IT TO.
 * The button renders a real `<a>`, which the container's own Link Click
 * trigger already reports off the DOM — the `data-link*` attributes the button
 * carries decide what that report SAYS, not whether it happens. Production
 * fires a second `link_click` from its own `onClick` and double-counts every
 * one of its 104 live CTAs; that is the defect this seam exists to not repeat.
 *
 * ⚠ ONE SEAM PER BUTTON. The click is caught by delegation, so a seam wrapped
 * around both CTAs would report the trailing button's click under the leading
 * button's wording and address.
 *
 * ⚠ The boundary is HERE rather than on the organism: this is the wrapper the
 * design system's rule prescribes for a call site that needs an `onClick`, so
 * the organism and every other sub-part stay Server Components and `children`
 * crosses already rendered. See design-conflicts/page-section-header.mdx for
 * the four-question argument.
 */
function SectionHeaderCtaImpl({ url, label, children }: SectionHeaderCtaProps) {
  /* `toArray` rather than `Children.count`, which counts the `null` and `false`
     a conditional slot leaves behind and would warn on a single button the
     organism renders beside one it did not. */
  if (process.env.NODE_ENV !== "production" && React.Children.toArray(children).length > 1) {
    console.error(
      "[SectionHeaderCta] is wrapped around more than one button, so every click inside it is reported under the first button's wording and address. Give each button its own SectionHeaderCta."
    );
  }

  /* No `useCallback`: this handler feeds a plain `<span>`, which is neither a
     memoized child nor an effect dependency, so a stable reference would buy
     nothing and cost a comparison on every render. */
  const handleClick = () => {
    const destination = typeof url === "string" ? url : "";

    /* Both tests are case-sensitive and read the whole address — see their own
       header in ./page-section-header.shared for why that is deliberate. */
    if (isPhoneCta(destination)) {
      firePhoneLead({ ctaLabel: label });
      return;
    }

    if (isPdfCta(destination)) {
      firePdfDownload({ documentName: label || destination });
    }

    /* Everything else falls through deliberately — see the note above. */
  };

  return (
    /* `.ctaSeam` is `display: contents`, so the seam takes no box: the button
       stays a direct child of the row the organism lays out, and there is no
       padding of our own for a click to land on that no button raised.
       ⚠ THAT RULE IS THE ONLY PLACE `display` MAY BE SET ON THIS ELEMENT —
       anything else in the stylesheet giving it a box reopens the dead region.
       ⚠ The push is deferred by one turn and the click NAVIGATES; what lands it
       is the service's own `pagehide` and `visibilitychange` flush. */
    <span className={styles.ctaSeam} onClick={handleClick}>
      {children}
    </span>
  );
}
export const SectionHeaderCta = withSafeRender(SectionHeaderCtaImpl, "SectionHeaderCta");

export default SectionHeaderCta;
